import {
  Col,
  Form, InputNumber, message,
  Row,
  Skeleton, Switch,
  Typography,
  Select
} from "antd";
import { FormComponent } from "components/BaseComponent";
import { ShelterSponsorMeForm } from "components/Forms/ShelterSettings";
import { LanguageConsumer } from "context/LanguageContext";
import { ShelterContextData } from "context/ShelterContext";
import { ShelterSettingContextData } from "context/ShelterSettingContext";
import IShelter from "interfaces/IShelter";
import IShelterSponsor from "interfaces/IShelterSponsor";
import React from "react";
import { ShelterSettingPetProfileSaveRequest, ShelterSettingSponsorSaveRequest } from "services/api/interfaces/ApiRequest";
import { featureConfigGet, featureConfigSave } from "services/api";

const SHOW_UPL_THRESHOLD_DAYS = false;

type PetProfileProps = {
  uuid: string;
  context: ShelterContextData;
  settingsContext: ShelterSettingContextData;
  data: IShelter;
  shelterSponsorSettings: IShelterSponsor | null;
  dispatchSaveSponsorSetting: (args: ShelterSettingSponsorSaveRequest) => Promise<any>
  
};

export default class PetProfile extends FormComponent<PetProfileProps> {
  state = {
    loaded: false,
    initialValues: {},
    fieldValues: {
      allow_adoption: {
        in_foster: true,
        in_shelter: true,
        type_key_lost: false,
        type_key_found: false,
        show_adoption_fee: false
      },
      features: {
        adoption: {
          status_key: "DISABLED",
          board: { upl: { enabled: false, threshold_days: null } }
        },
        lost: { status_key: "DISABLED", public_report: { enabled: true } },
        found: { status_key: "DISABLED", public_report: { enabled: true } }
      }
    } as any,
    savedFeatures: null as any,
  };

  form: any;

  async componentDidMount() {
    const { uuid, settingsContext } = this.props;

    let fetchedFeatures = {};
    if (uuid) {
      if (!settingsContext.fetching) {
        await settingsContext.requestShelterSettingList({
          search: {
            organization_shelter: {
              has: {
                uuid,
              },
            },
          },
        });
      }

      try {
        const featureRes = await featureConfigGet({
          organization_shelter_uuid: uuid
        });
        if (featureRes.ok && featureRes.data?.data?.resolved?.application) {
          fetchedFeatures = featureRes.data.data.resolved.application;
        }
      } catch (err) {
        console.error("Failed to load feature config", err);
      }
    }
    

    const fetchedAllowAdoption = this.getValues()?.allow_adoption;
    const allowAdoption = {
      ...this.state.fieldValues.allow_adoption,
      ...fetchedAllowAdoption,
    };
    
    const features = {
      adoption: { ...this.state.fieldValues.features.adoption, ...(fetchedFeatures as any)?.adoption },
      lost: { ...this.state.fieldValues.features.lost, ...(fetchedFeatures as any)?.lost },
      found: { ...this.state.fieldValues.features.found, ...(fetchedFeatures as any)?.found },
    };

    this.setState({
      loaded: true,
      initialValues: {
        ...allowAdoption,
        features
      },
      fieldValues: {
        allow_adoption: allowAdoption,
        features
      },
      savedFeatures: JSON.parse(JSON.stringify(features)),
    });
  }

  private getValues = () => {
    const { settingsContext } = this.props;

    const values = settingsContext.shelterSettings?.find(
      (v) => v.type_key === "PET_PROFILE"
    )?.value as any;
    return values;
  };

  private handleSave = async (values: any) => {

    const { uuid, settingsContext , dispatchSaveSponsorSetting} = this.props;
    const sponsor = values.shelter_setting?.sponsor || null

    let sponsorSetting = {} as any


    if (sponsor) {
      Object.keys(sponsor).forEach(key => {
        if (sponsor[key] !== undefined) {
          // Parse sponsor settings translation
          if (key === 'translation') {
            sponsorSetting[key] = values.shelter_setting.sponsor[key].map(p => {
              return {
                language_uuid: p.language,
                value: {
                  ...p.value,
                  enabled: values.shelter_setting.sponsor.enabled
                }
              }
            })
          } else {
            sponsorSetting[key] = sponsor[key]
          }
        }
      })
    }

    try {
      if (Object.keys(sponsorSetting).length) {
        await dispatchSaveSponsorSetting({
          shelter_uuid: uuid,
          type_key: 'SPONSOR',
          value: sponsorSetting,
          translation: sponsorSetting.translation || []
        })
      }


      const allowAdoptionSettings = this.state.fieldValues.allow_adoption;

      const payload: ShelterSettingPetProfileSaveRequest = {
        shelter_uuid: uuid,
        type_key: "PET_PROFILE",
        value: {
          allow_adoption: allowAdoptionSettings,
        },
      };

      await settingsContext.requestShelterSettingPetProfileSave(payload);
      
      const featuresData = this.buildFeaturesPayload();

      await featureConfigSave({
        scope: "shelter",
        scope_uuid: uuid,
        section: "application",
        data: featuresData
      });

      this.setState({ savedFeatures: JSON.parse(JSON.stringify(this.state.fieldValues.features)) });
      message.success("Pet profile saved successfully!");
    } catch (error) {
      this.restoreSavedFeatures();
      message.error(`Failed to save pet profile. Reason: ${error}`);
    }
  };

  private restoreSavedFeatures = () => {
    const { savedFeatures, fieldValues } = this.state;

    if (!savedFeatures) return;

    const features = JSON.parse(JSON.stringify(savedFeatures));

    this.setState({
      fieldValues: {
        ...fieldValues,
        features
      },
    });
    this.form?.setFieldsValue({ features });
  };

  private buildFeaturesPayload = () => {
    const features = JSON.parse(JSON.stringify(this.state.fieldValues.features || {}));
    const adoption = features.adoption;

    if (adoption?.board) {
      if (adoption.board.sort === "") {
        adoption.board.sort = null;
      } else if (adoption.board.sort === undefined) {
        delete adoption.board.sort;
      }

      const upl = adoption.board.upl;

      if (upl && Object.keys(upl).length) {
        upl.enabled = upl.enabled === true;

        if (SHOW_UPL_THRESHOLD_DAYS) {
          upl.threshold_days =
            upl.threshold_days === undefined || upl.threshold_days === "" || upl.threshold_days === null
              ? null
              : Number(upl.threshold_days);
        } else {
          delete upl.threshold_days;
        }
      } else if (upl !== null) {
        delete adoption.board.upl;
      }

      if (!Object.keys(adoption.board).length) {
        delete adoption.board;
      }
    }

    return features;
  };

  private setFeatureValue = (path: string[], value: any) => {
    const { features } = this.state.fieldValues;
    const [flow, ...rest] = path;

    if (!features[flow] || !rest.length) return;

    let target = features[flow];
    rest.slice(0, -1).forEach((key) => {
      if (typeof target[key] !== "object" || target[key] === null) {
        target[key] = {};
      }
      target = target[key];
    });

    target[rest[rest.length - 1]] = value;
  };

  private handleChange = async (values: any[], allValues?: any[]) => {
    const { allow_adoption, features } = this.state.fieldValues;
    for await (const v of values) {
      const fieldName = v.name[0];
      if (fieldName === "features") {
        this.setFeatureValue(v.name.slice(1), v?.value);
      } else {
        allow_adoption[fieldName] = v?.value;
      }
    }
    this.setState({
      fieldValues: {
        allow_adoption,
        features
      },
    });
  };

  render() {
    const { loaded, initialValues, fieldValues } = this.state;

    if (!loaded) {
      return <Skeleton />;
    }

    const uplEnabled = fieldValues.features?.adoption?.board?.upl?.enabled === true;
    const lostLive = fieldValues.features?.lost?.status_key === "LIVE";
    const foundLive = fieldValues.features?.found?.status_key === "LIVE";

    const values = {
      ...initialValues,
      shelter_setting: {
        sponsor: {
          enabled: true,
          btnText: "My Wishlist",
          ...(this.props.shelterSponsorSettings
            ? {
                ...this.props.shelterSponsorSettings,
                translation:
                  this.props.shelterSponsorSettings.translation?.map((p) => {
                    return {
                      language: p.language_uuid,
                      value: p.value,
                    };
                  }) || [],
              }
            : {}),
        } as any,
      },
    };


    return (
      <div>
        <Form
          name="pet_profile"
          layout="vertical"
          initialValues={values}
          ref={(ref) => {
            if (!this.form) {
              this.form = ref;
              this.forceUpdate();
            }
          }}
          onFinish={this.handleSave}
          onFieldsChange={this.handleChange}
          style={{ width: "100%" }}
        >
          <Row gutter={24}>
            <Col xs={24} sm={12} md={12}>
              <Typography.Title level={5}>
                Configuration of Adopt Me CTA
              </Typography.Title>
              <Typography.Text>
                Allows the Adopt Me CTA removal for In Shelter and In Foster
                pets separately. Example: this can be common for shelters that
                want to receive online adoption inquiries for animals that are
                In Foster but want In Shelter animals to receive
                inquiries/applications directly from adopters scanning the
                Adopets smart kennel card.
              </Typography.Text>
              <br />
              <br />
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12}>
                  <Form.Item
                    label="In Shelter"
                    name={["in_shelter"]}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12}>
                  <Form.Item
                    label="In Foster"
                    name={["in_foster"]}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
              <LanguageConsumer>
                {({ fetching, languages, requestLanguageList }) => {
                  return this.form ? (
                    <ShelterSponsorMeForm
                      form={this.form}
                      contentLoading={fetching}
                      shelter={this.props.data}
                      languages={languages}
                      loadLanguages={requestLanguageList}
                    />
                  ) : null;
                }}
              </LanguageConsumer>
            </Col>
            <Col xs={24} sm={12} md={12}>
              <Typography.Title level={5}>
                Configuration of CLAIM CTA
              </Typography.Title>
              <Typography.Text>
                Extends CTA removal controls to Lost and Found pets. In addition to configuring the Adopt Me button for In Shelter and In Foster animals, organizations can now remove the 'This is My Pet' button from Lost and/or Found pet profiles — useful for shelters that prefer to handle owner reunification inquiries through direct contact rather than the standard claim flow.
              </Typography.Text>
              <br />
              <br />
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12}>
                  <Form.Item
                    label="LOST"
                    name={["type_key_lost"]}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12}>
                  <Form.Item
                    label="FOUND"
                    name={["type_key_found"]}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={24} sm={12} md={12}>
              <Typography.Title level={5}>
                Show Adoption Fee
              </Typography.Title>
              <Typography.Text>
                
              </Typography.Text>
              <br />
              <br />
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12}>
                  <Form.Item
                    label="Show Adoption Fee"
                    name={["show_adoption_fee"]}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col xs={24} sm={12} md={12}>
              <Typography.Title level={5}>
                Feature Configuration
              </Typography.Title>
              <Typography.Text>
                Configure the visibility of features. <b>LIVE</b> appears in Adopter and Organization. <b>ONBOARDING</b> appears only in Organization. <b>DISABLED</b> appears nowhere.
              </Typography.Text>
              <br />
              <br />
              <Row gutter={24}>
                <Col xs={24} sm={8} md={8}>
                  <Form.Item
                    label="Adoption"
                    name={["features", "adoption", "status_key"]}
                  >
                    <Select>
                      <Select.Option value="LIVE">LIVE</Select.Option>
                      <Select.Option value="ONBOARDING">ONBOARDING</Select.Option>
                      <Select.Option value="DISABLED">DISABLED</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={8}>
                  <Form.Item
                    label="Lost"
                    name={["features", "lost", "status_key"]}
                  >
                    <Select>
                      <Select.Option value="LIVE">LIVE</Select.Option>
                      <Select.Option value="ONBOARDING">ONBOARDING</Select.Option>
                      <Select.Option value="DISABLED">DISABLED</Select.Option>
                    </Select>
                  </Form.Item>
                  {lostLive && (
                    <Form.Item
                      label="Accept public reports"
                      name={["features", "lost", "public_report", "enabled"]}
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  )}
                </Col>
                <Col xs={24} sm={8} md={8}>
                  <Form.Item
                    label="Found"
                    name={["features", "found", "status_key"]}
                  >
                    <Select>
                      <Select.Option value="LIVE">LIVE</Select.Option>
                      <Select.Option value="ONBOARDING">ONBOARDING</Select.Option>
                      <Select.Option value="DISABLED">DISABLED</Select.Option>
                    </Select>
                  </Form.Item>
                  {foundLive && (
                    <Form.Item
                      label="Accept public reports"
                      name={["features", "found", "public_report", "enabled"]}
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={24} sm={12} md={12}>
              <Typography.Title level={5}>
                Board
              </Typography.Title>
              <Typography.Text>
                Defines the order of the adoption list adopters see on the public pages and on the iframe.
              </Typography.Text>
              <br />
              <br />
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12}>
                  <Form.Item
                    label="Sort pets by"
                    name={["features", "adoption", "board", "sort"]}
                  >
                    <Select>
                      <Select.Option value="">Default (newest first)</Select.Option>
                      <Select.Option value="CREATED_DESC">Newest first</Select.Option>
                      <Select.Option value="CREATED_ASC">Oldest first</Select.Option>
                      <Select.Option value="LENGTH_STAY_ASC">Longest in shelter first</Select.Option>
                      <Select.Option value="APPLICATION_BALANCE_ASC">Fewest applications first</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={24} sm={12} md={12}>
              <Typography.Title level={5}>
                Urgent Placement (UPL)
              </Typography.Title>
              <Typography.Text>
                Enables the Urgent Placement board and the "Urgent Placement" badge on the public pet profile. When on, pets classified as urgent by the shelter staff are highlighted to adopters. The threshold restricts it to pets that have been in the shelter for at least the given number of days — leave it empty for no cut.
              </Typography.Text>
              <br />
              <br />
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12}>
                  <Form.Item
                    label="Enable Urgent Placement"
                    name={["features", "adoption", "board", "upl", "enabled"]}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>

                {SHOW_UPL_THRESHOLD_DAYS && (
                  <Col xs={24} sm={12} md={12}>       
                  <Form.Item
                    label="Length of stay threshold (days)"
                    name={["features", "adoption", "board", "upl", "threshold_days"]}
                    extra={
                      uplEnabled
                        ? undefined
                        : "Turning Urgent Placement off keeps this value for when it is turned back on."
                    }
                  >
                    <InputNumber
                      min={1}
                      precision={0}
                      placeholder="No cut"
                      disabled={!uplEnabled}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>)}
              </Row>
            </Col>
          </Row>


        </Form>
      </div>
    );
  }
}
